import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'localization';
import type { DataTableSortEvent, DataTableRowToggleEvent } from 'primereact/datatable';
import DataGridAtom from '../../atoms/DataGrid/DataGridAtom';
import type { DataGridColumn, DataGridAction } from '../../atoms/DataGrid/DataGridAtom';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import DataListHeader from './DataListHeader';
import DataListMobileView from './DataListMobileView';
import DataListSettingsPanel from './DataListSettingsPanel';
import { useDataListResponsive } from './useDataListResponsive';
import { useSphereColumns } from './useSphereColumns';
import { useDataListSettings } from './useDataListSettings';
import type { BasicSphereInfo } from './useSphereColumns';
import type { FilterExpression, PerspectiveFilter } from 'types/filters';
import type { PerspectiveFilterState } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import ColumnFilterHeaderAtom from '../../atoms/ColumnFilterHeader/ColumnFilterHeaderAtom';
import FilterChipsDisplayMolecule from '../FilterChipsDisplay/FilterChipsDisplayMolecule';
import './DataListMolecule.scss';

export interface DataListMoleculeProps<T = unknown> {
  // Data
  /** Array of data items to display */
  data: T[];
  /** Column definitions for the data grid */
  columns: DataGridColumn[];
  /** Whether data is loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
  /**
   * A designed error state (what failed, why, how to recover) that replaces the table and the
   * mobile cards while the header, search and filters stay in place. Takes precedence over `error`.
   */
  errorContent?: React.ReactNode;
  /** Message to show when data is empty */
  emptyMessage?: string;
  /** Unique key field for data items */
  dataKey?: string;

  // Header
  /** Title displayed in the header */
  title: string;
  /** Icon class for the header (e.g., "pi pi-list") */
  icon?: string;
  /** Whether the content can be collapsed */
  collapsible?: boolean;
  /** Default expanded state */
  defaultExpanded?: boolean;

  // Header Actions
  /** Show search input in header */
  showSearch?: boolean;
  /** Current search value (controlled) */
  searchValue?: string;
  /** Callback when search value changes */
  onSearchChange?: (value: string) => void;
  /** Search input placeholder text */
  searchPlaceholder?: string;

  /** Show refresh button */
  showRefresh?: boolean;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;

  /** Show settings button (requires settingsKey for built-in settings) */
  showSettings?: boolean;
  /** Unique key for persisting settings to localStorage (e.g., "context-inventory") */
  settingsKey?: string;
  /** Custom settings content rendered inside the settings panel */
  customSettings?: React.ReactNode;

  /** Additional custom header actions */
  headerActions?: React.ReactNode;

  /** Custom filter controls rendered in the header between search and table content */
  customFilters?: React.ReactNode;

  // Column Filters
  /** Active property filters for column filter chip display */
  propertyFilters?: FilterExpression[];
  /** Active perspective filters (raw) for column header active state */
  perspectiveFilters?: PerspectiveFilter[];
  /** Active perspective filters as PerspectiveFilterState for FilterChipsDisplayMolecule */
  perspectiveFiltersAsState?: PerspectiveFilterState[];
  /** Remove a property filter by field name */
  onRemoveColumnFilter?: (field: string) => void;
  /** Remove a perspective filter by system name */
  onRemovePerspectiveFilter?: (perspectiveSystemName: string) => void;
  /** Clear all column filters */
  onClearColumnFilters?: () => void;
  /** Get active property filter for a column field */
  getColumnFilter?: (field: string) => FilterExpression | undefined;
  /** Property filter changed callback */
  onColumnFilterChange?: (field: string, expression: FilterExpression | null) => void;
  /** Perspective filter changed callback */
  onPerspectiveFilterChange?: (perspectiveSystemName: string, assignees: string[]) => void;

  // Table Features
  /** Row action buttons */
  actions?: DataGridAction[];
  /** Whether to show action buttons column */
  showActions?: boolean;
  /** Enable pagination */
  paginator?: boolean;
  /** Number of rows per page */
  rows?: number;
  /** Options for rows per page dropdown */
  rowsPerPageOptions?: number[];
  /** Current sort field (controlled) */
  sortField?: string;
  /** Current sort order (controlled) */
  sortOrder?: 1 | -1;
  /** Callback when sort changes */
  onSort?: (event: DataTableSortEvent) => void;
  /** Whether to show table header row */
  showTableHeader?: boolean;

  /**
   * Hide the molecule's built-in title/search/refresh/settings bar.
   * Consumers can render their own toolbar above the table and still
   * use settings, search, etc. via the exposed callbacks.
   */
  hideHeader?: boolean;

  /** Controlled settings-panel visibility (overrides internal state when defined) */
  settingsPanelVisible?: boolean;
  /** Called when the settings panel wants to open/close */
  onSettingsPanelVisibleChange?: (visible: boolean) => void;

  // Row Expansion
  /** Template for rendering expanded row content */
  rowExpansionTemplate?: (data: T) => React.ReactNode;
  /** Object whose keys are the dataKey values of expanded rows */
  expandedRows?: Record<string, boolean>;
  /** Callback when expanded rows change */
  onRowToggle?: (event: DataTableRowToggleEvent) => void;

  // Mobile
  /** Breakpoint (in pixels) below which mobile view is shown */
  mobileBreakpoint?: number;
  /** Template function to render mobile cards. If not provided, table is shown at all sizes. */
  mobileCardTemplate?: (item: T, index: number) => React.ReactNode;

  // Styling
  /** Visual variant: 'atlas' for pill rows with shadows, 'standard' for traditional table */
  variant?: 'atlas' | 'standard';
  /** Additional CSS class name */
  className?: string;
}

/**
 * Renders a sphere identity cell using the lookup data
 */
function SphereCell({
  sphereId,
  sphereLookup
}: Readonly<{
  sphereId: string | null | undefined;
  sphereLookup: Record<string, BasicSphereInfo>;
}>) {
  if (!sphereId) {
    return <span className="data-list-sphere-cell data-list-sphere-cell--empty">—</span>;
  }

  const sphereInfo = sphereLookup[sphereId];

  if (!sphereInfo) {
    // Still loading or not found - show abbreviated ID
    return (
      <span className="data-list-sphere-cell data-list-sphere-cell--loading" title={sphereId}>
        {sphereId.slice(0, 8)}...
      </span>
    );
  }

  return (
    <span className="data-list-sphere-cell">
      <SphereIdentityAtom
        sphereId={sphereInfo.id}
        sphereName={sphereInfo.name}
        glyph={sphereInfo.glyph}
        glyphColor={sphereInfo.glyphColor}
        imageId={sphereInfo.imageId}
        size="small"
        isPrivate={sphereInfo.is_private}
      />
    </span>
  );
}

/**
 * Factory that creates a column body renderer for truncated text cells with hover tooltip.
 * Extracted to avoid defining components inside the render loop (S6478).
 */
function createTruncatedBody<T>(field: string) {
  return (rowData: T) => {
    const value = (rowData as Record<string, unknown>)[field];
    const text = value == null ? '' : `${value as string | number}`;
    return (
      <span className="truncate-cell" title={text}>
        {text}
      </span>
    );
  };
}

/**
 * Factory that creates a column body renderer for sphere identity cells.
 * Extracted to avoid defining components inside the render loop (S6478).
 */
function createSphereCellBody<T>(field: string, sphereLookup: Record<string, BasicSphereInfo>) {
  return (rowData: T) => {
    const sphereId = (rowData as Record<string, unknown>)[field] as string | null | undefined;
    return <SphereCell sphereId={sphereId} sphereLookup={sphereLookup} />;
  };
}

function DataListMolecule<T = unknown>({
  // Data
  data,
  columns,
  loading = false,
  error = null,
  errorContent,
  emptyMessage,
  dataKey,

  // Header
  title,
  icon,
  collapsible = false,
  defaultExpanded = true,

  // Header Actions
  showSearch = false,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showRefresh = false,
  onRefresh,
  showSettings = false,
  settingsKey,
  customSettings,
  headerActions,
  customFilters,

  // Column Filters
  propertyFilters,
  perspectiveFilters,
  perspectiveFiltersAsState,
  onRemoveColumnFilter,
  onRemovePerspectiveFilter,
  onClearColumnFilters,
  getColumnFilter,
  onColumnFilterChange,
  onPerspectiveFilterChange,

  // Table Features
  actions,
  showActions = false,
  paginator = false,
  rows = 10,
  rowsPerPageOptions = [5, 10, 25],
  sortField,
  sortOrder,
  onSort,
  showTableHeader = true,
  hideHeader = false,
  settingsPanelVisible: settingsPanelVisibleProp,
  onSettingsPanelVisibleChange,

  // Row Expansion
  rowExpansionTemplate,
  expandedRows,
  onRowToggle,

  // Mobile
  mobileBreakpoint = 768,
  mobileCardTemplate,

  // Styling
  variant = 'atlas',
  className = '',
}: Readonly<DataListMoleculeProps<T>>) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [settingsPanelVisibleInternal, setSettingsPanelVisibleInternal] = useState(false);
  const settingsPanelVisible = settingsPanelVisibleProp ?? settingsPanelVisibleInternal;
  const setSettingsPanelVisible = useCallback(
    (visible: boolean) => {
      if (onSettingsPanelVisibleChange) onSettingsPanelVisibleChange(visible);
      if (settingsPanelVisibleProp === undefined) setSettingsPanelVisibleInternal(visible);
    },
    [onSettingsPanelVisibleChange, settingsPanelVisibleProp],
  );
  const closeOpenFilterRef = useRef<(() => void) | null>(null);
  const handleFilterOpen = useCallback((closeMe: () => void) => {
    closeOpenFilterRef.current?.();
    closeOpenFilterRef.current = closeMe;
  }, []);

  const { isMobile, containerRef } = useDataListResponsive({
    mobileBreakpoint,
  });

  // Use settings hook when settingsKey is provided
  const settingsEnabled = showSettings && settingsKey;
  const {
    settings,
    updateSetting,
    toggleColumnVisibility,
    resetSettings,
    visibleColumnsList,
  } = useDataListSettings({
    settingsKey: settingsKey || 'default',
    columns,
    defaultRowsPerPage: rows,
    defaultSortField: sortField,
    defaultSortOrder: sortOrder === -1 ? 'desc' : 'asc',
  });

  // Use settings values when enabled, otherwise use props
  const effectiveRows = settingsEnabled ? settings.rowsPerPage : rows;
  const effectiveSortField = settingsEnabled ? settings.sortField : sortField;
  const settingsSortOrder = settings.sortOrder === 'desc' ? -1 : 1;
  const effectiveSortOrder = settingsEnabled
    ? settingsSortOrder
    : sortOrder;

  // Batch fetch sphere data for columns with isSphereId: true
  const { sphereLookup } = useSphereColumns(data, columns);

  // Get columns to display (filtered by visibility settings when enabled)
  const displayColumns = settingsEnabled ? visibleColumnsList : columns;

  // Create enhanced columns that auto-render spheres for isSphereId columns
  // and wrap filterable column headers with ColumnFilterHeaderAtom
  const enhancedColumns = useMemo(() => {
    return displayColumns.map(col => {
      let enhanced: DataGridColumn = col;

      // If column has isSphereId and no custom body, add sphere rendering
      if (col.isSphereId && !col.body) {
        enhanced = {
          ...enhanced,
          body: createSphereCellBody<T>(col.field, sphereLookup),
        };
      }

      // If column has truncateWithTooltip and no custom body, add truncated text rendering
      if (col.truncateWithTooltip && !col.body) {
        enhanced = {
          ...enhanced,
          body: createTruncatedBody<T>(col.field),
        };
      }

      // If column has filterType, wrap header with ColumnFilterHeaderAtom
      if (col.filterType && onColumnFilterChange) {
        const activeFilter = getColumnFilter?.(col.field);
        const activePerspFilter = col.filterPerspectiveSystemName
          ? perspectiveFilters?.find(
              (f) => f.perspectiveSystemName === col.filterPerspectiveSystemName,
            )
          : undefined;

        return {
          ...enhanced,
          header: (
            <ColumnFilterHeaderAtom
              label={typeof col.header === 'string' ? col.header : String(col.header)}
              filterType={col.filterType}
              enumValues={col.filterEnumValues}
              activeFilter={activeFilter}
              activePerspectiveFilter={activePerspFilter}
              onFilterOpen={handleFilterOpen}
              onFilterChange={(expression) => {
                if (expression) {
                  onColumnFilterChange(col.field, expression);
                } else {
                  onRemoveColumnFilter?.(col.field);
                }
              }}
              onPerspectiveFilterChange={
                col.filterPerspectiveSystemName && onPerspectiveFilterChange
                  ? (assignees) =>
                      onPerspectiveFilterChange(col.filterPerspectiveSystemName!, assignees)
                  : undefined
              }
              placeholder={col.filterPlaceholder}
            />
          ),
        } as DataGridColumn;
      }

      return enhanced;
    });
  }, [
    displayColumns,
    sphereLookup,
    onColumnFilterChange,
    onRemoveColumnFilter,
    getColumnFilter,
    perspectiveFilters,
    onPerspectiveFilterChange,
    handleFilterOpen,
  ]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleSettingsClick = useCallback(() => {
    setSettingsPanelVisible(true);
  }, []);

  const handleSettingsPanelHide = useCallback(() => {
    setSettingsPanelVisible(false);
  }, []);

  const computedEmptyMessage = emptyMessage || t('dataList.emptyMessage', 'No data available');

  const shouldShowMobile = isMobile && mobileCardTemplate;

  // Column filter label resolvers for FilterChipsDisplay
  const getPropertyLabel = useCallback((propertyKey: string): string => {
    const col = columns.find(c => c.field === propertyKey);
    return typeof col?.header === 'string' ? col.header : propertyKey;
  }, [columns]);

  const getPropertyValueLabel = useCallback((propertyKey: string, value: string): string => {
    const col = columns.find(c => c.field === propertyKey);
    if (col?.filterEnumValues) {
      const enumVal = col.filterEnumValues.find(v => v.value === value);
      return enumVal?.label ?? value;
    }
    return value;
  }, [columns]);

  const getPerspectiveLabel = useCallback((perspectiveSystemName: string): string => {
    const col = columns.find(c => c.filterPerspectiveSystemName === perspectiveSystemName);
    return typeof col?.header === 'string' ? col.header : perspectiveSystemName;
  }, [columns]);

  const handleRemovePropertyFilter = useCallback((index: number) => {
    const filter = propertyFilters?.[index];
    if (filter) onRemoveColumnFilter?.(filter.property);
  }, [propertyFilters, onRemoveColumnFilter]);

  const variantClass = variant === 'atlas' ? 'data-list-molecule--atlas' : 'data-list-molecule--standard';

  return (
    <div
      ref={containerRef}
      className={`data-list-molecule ${variantClass} ${className}`}
      data-testid="data-list-molecule"
    >
      {!hideHeader && (
        <DataListHeader
          title={title}
          icon={icon}
          collapsible={collapsible}
          isExpanded={isExpanded}
          onToggle={handleToggle}
          itemCount={data.length}
          showSearch={showSearch}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          showRefresh={showRefresh}
          onRefresh={onRefresh}
          refreshing={loading}
          showSettings={showSettings}
          onSettingsClick={handleSettingsClick}
          headerActions={headerActions}
          customFilters={customFilters}
        />
      )}

      {onClearColumnFilters && (isExpanded || !collapsible) && (
        <FilterChipsDisplayMolecule
          propertyFilters={propertyFilters}
          perspectiveFilters={perspectiveFiltersAsState}
          onRemovePropertyFilter={handleRemovePropertyFilter}
          onRemovePerspectiveFilter={onRemovePerspectiveFilter}
          onClearAll={onClearColumnFilters}
          getPropertyLabel={getPropertyLabel}
          getPropertyValueLabel={getPropertyValueLabel}
          getPerspectiveLabel={getPerspectiveLabel}
          compact
        />
      )}

      {(isExpanded || !collapsible) && (
        <div className="data-list-content">
          {errorContent}
          {!errorContent && (shouldShowMobile ? (
            <DataListMobileView
              data={data}
              cardTemplate={mobileCardTemplate}
              emptyMessage={computedEmptyMessage}
              loading={loading}
            />
          ) : (
            <DataGridAtom
              data={data}
              columns={enhancedColumns}
              loading={loading}
              error={error}
              emptyMessage={computedEmptyMessage}
              actions={actions}
              showActions={showActions}
              paginator={paginator}
              rows={effectiveRows}
              rowsPerPageOptions={rowsPerPageOptions}
              sortField={effectiveSortField || undefined}
              sortOrder={effectiveSortOrder}
              onSort={onSort}
              showHeader={showTableHeader}
              dataKey={dataKey}
              rowExpansionTemplate={rowExpansionTemplate}
              expandedRows={expandedRows}
              onRowToggle={onRowToggle}
              className="data-list-data-grid"
              scrollable
              stripedRows
            />
          ))}
        </div>
      )}

      {/* Built-in settings panel */}
      {settingsEnabled && (
        <DataListSettingsPanel
          visible={settingsPanelVisible}
          onHide={handleSettingsPanelHide}
          settings={settings}
          columns={columns}
          onToggleColumn={toggleColumnVisibility}
          onUpdateSetting={updateSetting}
          onReset={resetSettings}
          rowsPerPageOptions={rowsPerPageOptions}
          customSettings={customSettings}
        />
      )}
    </div>
  );
}

export default DataListMolecule;
